<template lang="pug">
    v-card.mx-lg-auto.mt-md-4.mb-md-4(outlined width="100%" v-if="validCommunicationDisposition")
        v-toolbar.disposition-history-toolbar(flat)
            v-btn(
                color="primary"
                icon
                ripple
                @click="showDetails = !showDetails;"
            )
                v-icon(v-if="showDetails === true")
                    | mdi-chevron-down
                v-icon(v-else)
                    | mdi-chevron-right

            .disposition-history-title
                .disposition-history-title__primary
                    span.disposition-history-title__primary-text {{ primaryTitle }}
                    template(v-if="hasPulseDisposition")
                        v-tooltip(bottom)
                            template(v-slot:activator="{ on, attrs }")
                                PulseColorIcon.disposition-history-pulse(
                                    v-bind="attrs"
                                    v-on="on"
                                    :size="22"
                                )
                            span Pulse disposition available
                .disposition-history-title__disposition(v-if="showCampaign")
                    span.disposition-history-title__disposition-label Disposition:
                    span.disposition-history-title__disposition-value {{ collapsedDispositionLabel }}
            v-spacer
            .meta-info
                .meta-channel {{ communicationDisposition.communication_channel }}
                .meta-date {{ metaDate }}
                template(v-if="metaTime.length > 0")
                    .meta-time {{ metaTime }}

        template(v-if="showDetails === true")
            v-divider
            v-card-text
                v-simple-table
                    template(v-slot:default)
                        tbody
                            tr
                                td
                                    .text-capitalize.text-overline.text--secondary.text--darken-3 Organization
                                    .cell-value-spacing {{ communicationDisposition.organization_name }}
                            tr
                                td
                                    .text-capitalize.text-overline.text--secondary.text--darken-3 Campaign
                                    .cell-value-spacing(v-if="communicationDisposition.campaign_name && communicationDisposition.campaign_name.length > 0") {{ communicationDisposition.campaign_name }}
                                    .cell-value-spacing(v-else) N/a
                            tr
                                td
                                    .text-capitalize.text-overline.text--secondary.text--darken-3 Agent
                                    .cell-value-spacing(v-if="communicationDisposition.user_full_name && communicationDisposition.user_full_name.length > 0") {{ communicationDisposition.user_full_name }}
                                    .cell-value-spacing(v-else) N/a
                            tr
                                td
                                    .text-capitalize.text-overline.text--secondary.text--darken-3 Datetime
                                    .cell-value-spacing {{ communicationDisposition.friendly_created_at_time }}
                            tr
                                td
                                    .text-capitalize.text-overline.text--secondary.text--darken-3 Disposition
                                    .cell-value-spacing(v-if="communicationDisposition.disposition_description && communicationDisposition.disposition_description.length > 0") {{ communicationDisposition.disposition_description }}
                                    .cell-value-spacing(v-else) N/a
                            tr
                                td
                                    .text-capitalize.text-overline.text--secondary.text--darken-3 Communication Channel
                                    .cell-value-spacing.text-capitalize(v-if="communicationDisposition.communication_channel && communicationDisposition.communication_channel.length > 0") {{ communicationDisposition.communication_channel }}
                                    .cell-value-spacing(v-else) N/a
                            tr
                                td
                                    .text-capitalize.text-overline.text--secondary.text--darken-3 Notes
                                    .cell-value-spacing(v-if="communicationDisposition.notes && communicationDisposition.notes.length > 0") {{ communicationDisposition.notes }}
                                    .cell-value-spacing(v-else) N/a
                template(v-if="aiCommunicationDispositions.length > 0")
                    .pulse-disposition-section
                        PulseDispositionComparison(
                            :agent-disposition="communicationDisposition.disposition_description"
                            :agent-notes="communicationDisposition.notes"
                            :ai-communication-dispositions="aiCommunicationDispositions"
                            :conference-vid="communicationDisposition.conference_key"
                            stacked
                        )

</template>

<script lang="ts">
    import Vue from 'vue';

    // Components
    import PulseColorIcon from '@/components/scorecards/pulse_color_icon.vue';
    import PulseDispositionComparison from '@/components/shared/pulse/pulse_disposition_comparison.vue';

    // Helpers
    import { buildAiCommunicationDispositions } from "@/helpers/pulse";

    export default Vue.extend({
        data() {
            return {
                showDetails: false,
            }
        },
        computed: {
            aiCommunicationDispositions() {
                return buildAiCommunicationDispositions(this.communicationDisposition);
            },
            collapsedDispositionLabel() {
                const description = this.communicationDisposition?.disposition_description;
                return description?.length > 0 ? description : 'N/a';
            },
            hasPulseDisposition() {
                return this.aiCommunicationDispositions.length > 0;
            },
            primaryTitle() {
                const title = this.showCampaign ? this.communicationDisposition?.campaign_name : this.communicationDisposition?.disposition_description;
                return title?.length > 0 ? title : 'N/a';
            },
            metaDate() {
                return this.metaParts[0] || '';
            },
            metaParts() {
                return (this.communicationDisposition?.friendly_created_at_date || '').trim().split(/\s+/);
            },
            metaTime() {
                if (this.metaParts.length <= 1) {
                    return '';
                }

                return this.metaParts.slice(1).join(' ');
            },
            validCommunicationDisposition() {
                return this.communicationDisposition?.communication_disposition_id > 0;
            },
        },
        components: {
            PulseColorIcon,
            PulseDispositionComparison,
        },
        props: {
            communicationDisposition: Object,
            showCampaign: Boolean,
        },
    });
</script>

<style lang="scss" scoped>
    .disposition-history-pulse {
        display: inline-flex;
        flex: 0 0 auto;
        margin-left: 6px;
    }

    .disposition-history-title {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        padding-right: 12px;

        &__primary {
            align-items: center;
            display: flex;
            font-size: 1rem;
            font-weight: 500;
            line-height: 1.25;
            min-width: 0;
        }

        &__primary-text {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__disposition {
            align-items: baseline;
            display: flex;
            font-size: 0.75rem;
            gap: 4px;
            line-height: 1.2;
            min-width: 0;
            padding-top: 2px;
        }

        &__disposition-label {
            flex: 0 1 auto;
            font-weight: 700;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__disposition-value {
            flex: 1 1 0;
            font-weight: 400;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

    }

    .disposition-history-toolbar {
        min-width: 0;
    }

    .meta-info {
        flex: 0 0 auto;
        margin-left: 16px;
        text-align: right;
        
        .meta-channel {
            font-size: 0.75rem;
            font-weight: 700;
            text-transform: uppercase;
            line-height: 1.2;
            margin-bottom: 2px;
        }
        
        .meta-date,
        .meta-time {
            font-size: 0.75rem;
            font-weight: 400;
            line-height: 1.2;
        }
    }

    #app {
        .cell-value-spacing {
            padding: 0 0 5px 0;
        }
        .pulse-disposition-section {
            padding: 8px 16px 4px;
        }
        .m-l-5 {
            margin-left: 5px;
        }
    }
</style>